import React from 'react'
import type { NotifyPermission } from '../types'

const createNotification = (title: string, body: string) => {
  if (typeof window === 'undefined') return null
  if (!('Notification' in window)) return null
  if (Notification.permission !== 'granted') return null
  return new Notification(title, { body })
}

type UseExportNotificationsResult = {
  notifyPanelOpen: boolean
  notifyEnabled: boolean
  notifySupported: boolean
  notifyPermission: NotifyPermission
  requestingPermission: boolean
  notifyError: string | null
  handleOpenNotifyPanel: () => void
  handleEnableNotify: () => Promise<void>
}

const useExportNotifications = (open: boolean): UseExportNotificationsResult => {
  const [notifyPanelOpen, setNotifyPanelOpen] = React.useState(false)
  const [notifyEnabled, setNotifyEnabled] = React.useState(false)
  const [notifySupported, setNotifySupported] = React.useState(false)
  const [notifyPermission, setNotifyPermission] = React.useState<NotifyPermission>('default')
  const [requestingPermission, setRequestingPermission] = React.useState(false)
  const [notifyError, setNotifyError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setNotifyPanelOpen(false)
      return undefined
    }

    const supported = typeof window !== 'undefined' && 'Notification' in window
    setNotifySupported(supported)

    if (supported) {
      const permission = Notification.permission as NotifyPermission
      setNotifyPermission(permission)
      setNotifyEnabled(permission === 'granted')
    } else {
      setNotifyPermission('default')
      setNotifyEnabled(false)
    }

    return undefined
  }, [open])

  const prevOpenRef = React.useRef(open)
  React.useEffect(() => {
    const prevOpen = prevOpenRef.current
    prevOpenRef.current = open

    if (prevOpen && !open && notifyEnabled) {
      const notification = createNotification('Export VPauto', "L'export est terminé ✅")
      if (notification) window.setTimeout(() => notification.close(), 5000)
    }

    return undefined
  }, [open, notifyEnabled])

  const handleOpenNotifyPanel = React.useCallback(() => {
    setNotifyPanelOpen(true)

    if (typeof window !== 'undefined' && 'Notification' in window) {
      const permission = Notification.permission as NotifyPermission
      setNotifyPermission(permission)
      setNotifyEnabled(permission === 'granted')
    }
  }, [])

  const handleEnableNotify = React.useCallback(async () => {
    setNotifyError(null)

    if (!notifySupported) {
      setNotifyError('Les notifications ne sont pas supportées par ce navigateur.')
      return
    }

    if (notifyPermission === 'granted') {
      setNotifyEnabled(true)
      return
    }

    if (requestingPermission) return

    try {
      setRequestingPermission(true)
      const permission = (await Notification.requestPermission()) as NotifyPermission
      setNotifyPermission(permission)

      if (permission === 'granted') {
        setNotifyEnabled(true)
        const notification = createNotification(
          'Export VPauto',
          "Vous serez notifié une fois l'export terminé ✅",
        )
        if (notification) window.setTimeout(() => notification.close(), 3500)
      } else {
        setNotifyEnabled(false)
        setNotifyError('Permission refusée. Activez-la dans les réglages du navigateur.')
      }
    } catch (error) {
      setNotifyEnabled(false)
      setNotifyError("Impossible d'activer les notifications.")
    } finally {
      setRequestingPermission(false)
    }
  }, [notifyPermission, notifySupported, requestingPermission])

  return {
    notifyPanelOpen,
    notifyEnabled,
    notifySupported,
    notifyPermission,
    requestingPermission,
    notifyError,
    handleOpenNotifyPanel,
    handleEnableNotify,
  }
}

export default useExportNotifications
